import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { getAdminApi } from '../services/api';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'features', label: 'Features' },
  { value: 'billing', label: 'Billing' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'how-to', label: 'How-To' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' }
];

const AdminKB = () => {
  const navigate = useAppNavigation();
  const routerNavigate = useNavigate();
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchEntries();
  }, [category, status]);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const adminApi = getAdminApi();
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (status) params.append('status', status);
      
      const response = await adminApi.get(`/kb?${params.toString()}`);
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching KB entries:', err);
      setError(err.response?.data?.error || 'Failed to load KB entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchEntries();
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const adminApi = getAdminApi();
      const response = await adminApi.post('/kb/search', {
        query: searchQuery,
        limit: 20
      });
      setEntries(response.data.results || []);
      setTotal(response.data.count || 0);
    } catch (err) {
      console.error('Error searching KB:', err);
      setError(err.response?.data?.error || 'Failed to search KB');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to archive this KB entry?')) {
      return;
    }
    
    try {
      const adminApi = getAdminApi();
      await adminApi.delete(`/kb/${entryId}`);
      fetchEntries();
    } catch (err) {
      console.error('Error deleting KB entry:', err);
      setError(err.response?.data?.error || 'Failed to delete KB entry');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'features': 'primary',
      'billing': 'secondary',
      'troubleshooting': 'error',
      'how-to': 'info',
      'account': 'warning',
      'other': 'default'
    };
    return colors[category] || 'default';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Knowledge Base
          </Typography>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => routerNavigate('/app/admin/kb/new')}
          >
            Add Entry
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Semantic search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <Button variant="outlined" onClick={handleSearch}>
              Search
            </Button>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {total} entries
            </Typography>
          </Box>
        </Paper>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Uses</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No KB entries found. Click "Add Entry" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {entry.question.length > 80 
                            ? entry.question.substring(0, 80) + '...' 
                            : entry.question}
                        </Typography>
                        {entry.similarity && (
                          <Typography variant="caption" color="text.secondary">
                            Relevance: {(entry.similarity * 100).toFixed(0)}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.category || 'other'} 
                          size="small"
                          color={getCategoryColor(entry.category)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.status} 
                          size="small"
                          color={getStatusColor(entry.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {entry.times_used || 0}
                      </TableCell>
                      <TableCell align="center">
                        {entry.priority || 0}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => routerNavigate(`/app/admin/kb/${entry.id}`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(entry.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
};

export default AdminKB;
